<template>
  <DiscountPopup v-if="discount" />
</template>

<script>
import { computed, defineAsyncComponent, onMounted } from 'vue';
import Tracking from '@/components/commons/Tracking';
import useExperiment from '@/compositions/useExperiment';
import useAsyncComputed from '@/compositions/useAsyncComputed.js';
import useDiscount from '@/compositions/useDiscount';
import useHead from '@/compositions/useHead';
import StructuredOrganization from '@/components/meta/StructuredOrganization.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import { useRouter } from 'vue-router';
import { resolveEntryMutating } from '@/services/ContentfulService.js';

export default {
  name: 'HomepageMeta',
  components: {
    DiscountPopup: defineAsyncComponent(() => import('@/components/home/DiscountPopup.vue')),
  },
  props: {
    entry: {
      type: Object,
      required: true,
    },
    isPreview: {
      type: Boolean,
      required: true,
    },
    slug: {
      type: String,
      required: false,
    },
  },
  async setup(props) {
    const router = useRouter();
    const promises = [];
    const DEFAULT_DESCRIPTION = "Great style is easy. Article offers stylish modern, mid century, and scandinavian furniture from world renowned designers at accessible prices. Shop now."

    const [seoImage, loadingSeoImage, initSeoImage] = useAsyncComputed(
        async () => (await resolveEntryMutating(props.entry.fields.seoImage, 2, props.isPreview))?.fields?.file,
    );
    promises.push(initSeoImage);

    const { discount, processUrlDiscount } = useDiscount();

    const { isFeatureVariant, onExperimentLoaded } = useExperiment();

    const discountName = router.currentRoute.value.params?.discount;

     const description = computed(() => !!props.entry.fields.description ? props.entry.fields.description : DEFAULT_DESCRIPTION);

    onExperimentLoaded(() => {
      if (isFeatureVariant('interior-design-tile', 'on')) {
        Tracking.track('interior-design-tile-home-page', {
          on: true,
        });
      }
    });

    onMounted(() => {
      Tracking.track('Home page load', { ecomm_pagetype: "Home"})
    });

    const metaTags = computed(() => {
      const tags = [
        {
          name: 'description',
          content: description.value,
        },
        ...buildSocialMetaTags({
          title: props.entry.fields.title,
          description: description.value,
          url: import.meta.env.VITE_STOREFRONT_BASE_URL,
          image: seoImage.value,
        }),
      ];

      if (discountName) {
        tags.push({ name: 'robots', content: 'noindex, nofollow' });
      }

      return tags;
    });

    useHead(
        {
          title: props.entry.fields.title,
          script: [{ type: 'application/ld+json', children: JSON.stringify(StructuredOrganization) }],
          meta: computed(() => metaTags.value),
        },
        { appendTitleSuffix: false },
    );

    if (discountName) {
      promises.push(processUrlDiscount({ discountName, campaignType: 'B2C' }));
    }
    await Promise.all(promises);

    return {
      discount,
    };
  },
};
</script>

<style lang="postcss"></style>

